import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Eye, EyeOff, ArrowLeft, UserPlus, User, Phone } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthService } from '../firebase';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: white;
  width: 100%;
  display: flex;
`;

const LeftSide = styled.div`
  flex: 1;
  background: url('/pictures/register.webp') center/cover;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightSide = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Container = styled.div`
  max-width: 400px;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const RegisterCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h1 {
    font-size: 1.8rem;
    color: #333;
    margin-bottom: 8px;
    font-weight: 300;
  }

  p {
    font-size: 1rem;
    color: #666;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  padding-right: 50px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #333;
  }

  &::placeholder {
    color: #999;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;

  &:hover {
    color: #333;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 10px 0;
`;

const Checkbox = styled.input`
  margin: 0;
  margin-top: 2px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  cursor: pointer;

  a {
    color: #333;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px 30px;
  background: #333;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.3s ease;

  &:hover {
    background: #555;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 30px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }
  
  span {
    padding: 0 20px;
    color: #999;
    font-size: 14px;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 30px;
  
  p {
    color: #666;
    margin: 0;
  }
  
  a {
    color: #333;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border-left: 4px solid #c62828;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #2e7d32;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border-left: 4px solid #2e7d32;
`;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [countryCode, setCountryCode] = useState('+90');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
      setError('Lütfen tüm alanları doldurun');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return false;
    }

    if (!agreeToTerms) {
      setError('Kullanım şartlarını kabul etmelisiniz');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      // Ülke kodu ve telefon numarasını birleştir
      const phoneNumber = `${countryCode} ${formData.phone}`;
      
      const result = await AuthService.signUp(formData.email, formData.password, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        phoneNumber: phoneNumber
      });

      if (result.success) {
        setSuccess('Kayıt başarılı! E-posta adresinize doğrulama linki gönderildi. Lütfen e-postanızı kontrol edin.');
        
        // Reset form after success
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: ''
          });
          setAgreeToTerms(false);
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error || 'Kayıt olurken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      setError('Kayıt olurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <LeftSide />
      <RightSide>
        <Container>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Geri Dön
        </BackButton>

        <RegisterCard>
          <Header>
            <h1>Kayıt Ol</h1>
            <p>Yeni hesap oluşturun</p>
          </Header>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>Ad</Label>
                <InputContainer>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Adınız"
                    required
                  />
                  <InputIcon>
                    <User size={20} />
                  </InputIcon>
                </InputContainer>
              </FormGroup>

              <FormGroup>
                <Label>Soyad</Label>
                <InputContainer>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Soyadınız"
                    required
                  />
                  <InputIcon>
                    <User size={20} />
                  </InputIcon>
                </InputContainer>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>E-posta</Label>
              <InputContainer>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ornek@email.com"
                  required
                />
                <InputIcon>
                  <Mail size={20} />
                </InputIcon>
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Telefon Numarası</Label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ position: 'relative', width: '120px' }}>
                  <select 
                    value={countryCode} 
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{ 
                      width: '100%',
                      padding: '15px 12px',
                      paddingRight: '8px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: 'white',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      fontWeight: '500',
                      color: '#333'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#333';
                      e.target.style.boxShadow = '0 0 0 3px rgba(0,0,0,0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="+90">🇹🇷 +90</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                  </select>
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#999'
                  }}>▼</div>
                </div>
                <InputContainer style={{ flex: 1 }}>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="5XX XXX XX XX"
                    required
                  />
                  <InputIcon>
                    <Phone size={20} />
                  </InputIcon>
                </InputContainer>
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Şifre</Label>
              <InputContainer>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="En az 6 karakter"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Şifre Tekrar</Label>
              <InputContainer>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Şifrenizi tekrar girin"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </InputContainer>
            </FormGroup>

            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="terms"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
              />
              <CheckboxLabel htmlFor="terms">
                <a href="/mesafeli-satis">Kullanım şartlarını</a> ve 
                <a href="/gizlilik"> gizlilik politikasını</a> okudum ve kabul ediyorum.
              </CheckboxLabel>
            </CheckboxContainer>

            <SubmitButton type="submit" disabled={isLoading}>
              <UserPlus size={20} />
              {isLoading ? 'Kayıt oluşturuluyor...' : 'Kayıt Ol'}
            </SubmitButton>
          </Form>

          <Divider>
            <span>veya</span>
          </Divider>

          <LinkText>
            <p>Zaten hesabınız var mı? <Link to="/login">Giriş yapın</Link></p>
          </LinkText>
        </RegisterCard>
        </Container>
      </RightSide>
    </PageContainer>
  );
};

export default RegisterPage;
